import React, { useRef } from 'react';
import {
  Button,
  Dialog,
  DialogContentContainer,
  Text,
  Icon,
} from '@vibe/core';
import { Person, Team, DropdownChevronDown } from '@vibe/icons';

// ─── Types ────────────────────────────────────────────────────────────────────

import type { GroupByMode } from '../../stores/filterStore';
export type { GroupByMode } from '../../stores/filterStore';

interface ResourceTypeOption {
  value: GroupByMode;
  label: string;
  icon: typeof Person;
}

const RESOURCE_TYPE_OPTIONS: ResourceTypeOption[] = [
  { value: 'assignees', label: 'Users', icon: Person },
  { value: 'teams', label: 'Teams', icon: Team },
  { value: 'teamWithUsers', label: 'Team with Users', icon: Team },
];

interface AssigneeTeamToggleProps {
  /** Currently active grouping mode */
  value: GroupByMode;
  /** Called when the user switches modes */
  onChange: (mode: GroupByMode) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Dropdown that selects the resource display type:
 * - Users: show individual user rows
 * - Teams: show team rows only (capacity = sum of members)
 * - Team with Users: team row first, then individual user rows underneath
 */
const AssigneeTeamToggle: React.FC<AssigneeTeamToggleProps> = ({
  value,
  onChange,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const activeOption =
    RESOURCE_TYPE_OPTIONS.find((o) => o.value === value) ??
    RESOURCE_TYPE_OPTIONS[0];

  return (
    <div className="rp-resource-type-dropdown" ref={triggerRef}>
      <Dialog
        zIndex={1000}
        content={
          <DialogContentContainer
            className="rp-resource-type-dropdown__content"
            size={DialogContentContainer.sizes.SMALL}
          >
            {RESOURCE_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`rp-resource-type-dropdown__option ${
                  option.value === value
                    ? 'rp-resource-type-dropdown__option--active'
                    : ''
                }`}
                onClick={() => onChange(option.value)}
              >
                <Icon
                  icon={option.icon}
                  iconSize={18}
                  className="rp-resource-type-dropdown__option-icon"
                />
                <Text type={Text.types.TEXT2}>{option.label}</Text>
              </button>
            ))}
          </DialogContentContainer>
        }
        hideTrigger={['clickoutside', 'esckey', 'click']}
        showTrigger={['click']}
        position="bottom-start"
      >
        <Button
          size={Button.sizes.SMALL}
          kind={Button.kinds.TERTIARY}
          className="rp-resource-type-dropdown__trigger"
        >
          <Icon
            icon={activeOption.icon}
            iconSize={16}
            className="rp-resource-type-dropdown__trigger-icon"
          />
          <span>{activeOption.label}</span>
          <Icon
            icon={DropdownChevronDown}
            iconSize={16}
            className="rp-resource-type-dropdown__chevron"
          />
        </Button>
      </Dialog>
    </div>
  );
};

export default AssigneeTeamToggle;
